import { StateCreator } from 'zustand';
import { produce } from 'immer';
import { createAppStore } from '@marriott/mi-store-utils';

/**
 * SearchForm renderedData state interface
 * Mirrors the existing SearchForm renderedData context structure
 */
export interface SearchFormRenderedDataState {
  nightCount: number;
  monthSelected: number;
  yearSelected: number;
  tabSelected: string;
}

/**
 * Actions available for the SearchForm renderedData store
 */
export interface SearchFormRenderedDataActions {
  /**
   * Set the night count
   */
  setNightCount: (count: number) => void;

  /**
   * Set the selected month
   */
  setMonthSelected: (month: number) => void;

  /**
   * Set the selected year
   */
  setYearSelected: (year: number) => void;

  /**
   * Set the selected tab
   */
  setTabSelected: (tabSelected: string) => void;

  /**
   * Initialize the SearchForm renderedData store with context data
   */
  initializeSearchFormRenderedData: (data: Partial<SearchFormRenderedDataState>) => void;

  /**
   * Check if the store is in its initial/default state
   */
  isInitialState: () => boolean;

  /**
   * Reset SearchForm renderedData store to default state
   */
  resetSearchFormRenderedData: () => void;
}

/**
 * Combined SearchForm renderedData store interface
 */
export type SearchFormRenderedDataStore = SearchFormRenderedDataState & SearchFormRenderedDataActions;

/**
 * Initial state for the SearchForm renderedData store
 * Matches the default values from the original context
 */
export const initialSearchFormRenderedDataState: SearchFormRenderedDataState = {
  nightCount: 1,
  monthSelected: 0,
  yearSelected: 2022,
  tabSelected: '',
};

/**
 * SearchForm renderedData Zustand store implementation
 *
 * This store manages SearchForm renderedData state to replace the renderedData Context
 * in SearchFormComponent. It maintains the same API surface for seamless migration.
 *
 * Key features:
 * - Exact same state properties as original context (nightCount, monthSelected, yearSelected, tabSelected)
 * - Actions with same signatures as context setters
 * - Initialization method for seamless migration from context
 * - Reset functionality for testing/cleanup
 * - Non-persistent store (form state should not persist across sessions)
 */
const searchFormRenderedDataStore: StateCreator<SearchFormRenderedDataStore> = (set, get) => ({
  ...initialSearchFormRenderedDataState,

  /**
   * Set the night count
   */
  setNightCount: (count: number) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.nightCount = count;
      })
    );
  },

  /**
   * Set the selected month
   */
  setMonthSelected: (month: number) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.monthSelected = month;
      })
    );
  },

  /**
   * Set the selected year
   */
  setYearSelected: (year: number) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.yearSelected = year;
      })
    );
  },

  /**
   * Set the selected tab
   */
  setTabSelected: (tabSelected: string) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.tabSelected = tabSelected;
      })
    );
  },

  /**
   * Initialize the SearchForm renderedData store with context data
   * This is called from the conditional hook when the store should be used
   */
  initializeSearchFormRenderedData: (data: Partial<SearchFormRenderedDataState>) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        if (data.nightCount !== undefined) state.nightCount = data.nightCount;
        if (data.monthSelected !== undefined) state.monthSelected = data.monthSelected;
        if (data.yearSelected !== undefined) state.yearSelected = data.yearSelected;
        if (data.tabSelected !== undefined) state.tabSelected = data.tabSelected;
      })
    );
  },

  /**
   * Check if the store is in its initial/default state
   */
  isInitialState: () => {
    const currentState = get();
    return (
      currentState.nightCount === initialSearchFormRenderedDataState.nightCount &&
      currentState.monthSelected === initialSearchFormRenderedDataState.monthSelected &&
      currentState.yearSelected === initialSearchFormRenderedDataState.yearSelected &&
      currentState.tabSelected === initialSearchFormRenderedDataState.tabSelected
    );
  },

  /**
   * Reset SearchForm renderedData store to default state
   * Useful for testing and cleanup scenarios
   */
  resetSearchFormRenderedData: () => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.nightCount = initialSearchFormRenderedDataState.nightCount;
        state.monthSelected = initialSearchFormRenderedDataState.monthSelected;
        state.yearSelected = initialSearchFormRenderedDataState.yearSelected;
        state.tabSelected = initialSearchFormRenderedDataState.tabSelected;
      })
    );
  },
});

/**
 * Zustand store hook for SearchForm renderedData
 *
 * Usage:
 * ```typescript
 * const searchFormStore = useSearchFormRenderedDataStore();
 * const nightCount = searchFormStore.nightCount;
 * searchFormStore.setNightCount(2);
 * ```
 *
 * Note: This store is non-persistent as form state should not persist across browser sessions
 */
export const useSearchFormRenderedDataStore = createAppStore(searchFormRenderedDataStore, {
  usePersistentStore: false,
});

/**
 * Store selector helpers for common use cases
 */
export const searchFormRenderedDataStoreSelectors = {
  nightCount: (state: SearchFormRenderedDataStore) => state.nightCount,
  monthSelected: (state: SearchFormRenderedDataStore) => state.monthSelected,
  yearSelected: (state: SearchFormRenderedDataStore) => state.yearSelected,
  tabSelected: (state: SearchFormRenderedDataStore) => state.tabSelected,
  setNightCount: (state: SearchFormRenderedDataStore) => state.setNightCount,
  setMonthSelected: (state: SearchFormRenderedDataStore) => state.setMonthSelected,
  setYearSelected: (state: SearchFormRenderedDataStore) => state.setYearSelected,
  setTabSelected: (state: SearchFormRenderedDataStore) => state.setTabSelected,
  initializeSearchFormRenderedData: (state: SearchFormRenderedDataStore) => state.initializeSearchFormRenderedData,
  resetSearchFormRenderedData: (state: SearchFormRenderedDataStore) => state.resetSearchFormRenderedData,
  isInitialState: (state: SearchFormRenderedDataStore) => state.isInitialState,
};
